/* eslint-disable consistent-return */
/* eslint-disable camelcase */
/**
 * @file loginView.jsx
 * @author Fernando Ruano
 * @description StepThree View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FileTextOutlined } from '@ant-design/icons';

// Components
import ButtonFilled from '@nephro/components/ButtonFilled';
import LoginTemplate from '@nephro/components/LoginTemplate';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import { termsAndConditions } from '@nephro/commons/helpers/string-helper';

// Styles
import styles from './styles.module.css';

function stepThreeView({ loading }) {
  const [firstClick, setFirstClick] = React.useState(false);
  const [fullScrolled, setFullScrolled] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  const listInnerRef = React.useRef();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const onNavigationBack = () => {
    history.replace('/login');
  };

  React.useEffect(() => {
    if (getStorageItem('user')) {
      onNavigationBack();
    }
  }, [listInnerRef]);

  React.useEffect(() => {
    if (listInnerRef.current) {
      const { scrollHeight, clientHeight } = listInnerRef.current;
      if (scrollHeight <= clientHeight) setFullScrolled(true);
    }
  }, [firstClick]);

  const onScroll = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 1) setFullScrolled(true);
    }
  };

  const onFirstClick = () => setFirstClick(true);

  const onFinishRegistration = () => {
    history.push('/registration/stepFour', location?.state);
  };

  return (
    <LoginTemplate
      loading={loading}
      showTitle={!firstClick}
      showContent={!firstClick}
      title="registration.lastStep"
      textContent="registration.lastStepContent">
      <>
        {!firstClick ? (
          <ButtonFilled
            key="2"
            type="blue"
            onActionClick={onFirstClick}
            textButton={translate('registration.terms')}
            classStyles={[styles.actionButton].join(' ')}
            iconComponent={<FileTextOutlined style={{ marginTop: -2 }} />}
          />
        ) : (
          <div className="-ml-4 -mr-3 lg:-mr-6 lg:-ml-7">
            <section
              className={styles.scrollFrame}
              onScroll={() => onScroll()}
              ref={listInnerRef}>
              <td
                dangerouslySetInnerHTML={{
                  __html: termsAndConditions
                }}
              />
            </section>
            <div className="flex justify-center items-center ml-4 mr-1">
              <Checkbox
                checked={checked}
                disabled={!fullScrolled}
                onChange={(e) => setChecked(e.target.checked)}>
                {translate('registration.acceptTerms')}
              </Checkbox>
            </div>
            <div className="flex flex-row justify-between mb-7 mt-5">
              <Button
                onClick={onNavigationBack}
                type="link"
                className={styles.stepThreeButton}>
                {translate('commons.cancel')}
              </Button>
              <Button
                onClick={onFinishRegistration}
                disabled={!checked}
                type={checked && 'primary'}
                className={styles.stepThreeButton}>
                {translate('registration.continue')}
              </Button>
            </div>
          </div>
        )}
      </>
    </LoginTemplate>
  );
}

/**
 * Component definition of proptypes.
 */
stepThreeView.propTypes = {
  patientActions: PropTypes.shape({
    updatePatientStatus: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool.isRequired
};

export default stepThreeView;
